import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { Button, Modal } from 'semantic-ui-react';
import { closePopup } from '../../lib/popup';

import DroppableTypes from '../../constants/DroppableTypes';
import ListContainer from '../../containers/ListContainer';
import CardModalContainer from '../../containers/CardModalContainer';
import ListAdd from './ListAdd';
//import List_ from '../../models/List';
import { ReactComponent as PlusMathIcon } from '../../assets/images/plus-math-icon.svg';

import styles from './Board.module.scss';

const parseDndId = (dndId) => dndId.split(':')[1];

const Board = React.memo(
  ({
    listIds,
    listReportDataById,
    isCardModalOpened,
    canEdit,
    onListCreate,
    onListMove,
    onCardMove,
  }) => {

    const [t] = useTranslation();
    const [isListAddOpened, setIsListAddOpened] = useState(false);
    const [pendingCardMove, setPendingCardMove] = useState(null);
    const [isMoveConflictModalOpen, setIsMoveConflictModalOpen] = useState(false);

    const wrapper = useRef(null);
    const prevPosition = useRef(null);

    const handleAddListClick = useCallback(() => {
      setIsListAddOpened(true);
    }, []);

    const handleAddListClose = useCallback(() => {
      setIsListAddOpened(false);
    }, []);

    const handleDragStart = useCallback(() => {
      closePopup();
      setPendingCardMove(null);
      setIsMoveConflictModalOpen(false);
    }, []);

    const formatYearMonth = useCallback(
      (year, month) => {
        if (!year || !month) {
          return '';
        }

        return t('format:listMonthYear', {
          defaultValue: 'yyyy LLLL',
          postProcess: 'formatDate',
          value: new Date(year, month - 1, 1),
        });
      },
      [t],
    );

    const moveConflictText = useMemo(() => {
      if (!pendingCardMove?.source || !pendingCardMove?.destination) {
        return null;
      }

      const fromText = formatYearMonth(pendingCardMove.source.year, pendingCardMove.source.month);
      const toText = formatYearMonth(pendingCardMove.destination.year, pendingCardMove.destination.month);
      if (!fromText || !toText) {
        return null;
      }

      return `${fromText} → ${toText}`;
    }, [pendingCardMove, formatYearMonth]);

    const handlePendingMoveCancel = useCallback(() => {
      setPendingCardMove(null);
      setIsMoveConflictModalOpen(false);
    }, []);

    const handlePendingMoveContinue = useCallback(() => {
      if (!pendingCardMove) {
        return;
      }

      onCardMove(pendingCardMove.cardId, pendingCardMove.toListId, pendingCardMove.toIndex);
      setPendingCardMove(null);
      setIsMoveConflictModalOpen(false);
    }, [pendingCardMove, onCardMove]);

    const handleDragEnd = useCallback(
      ({ draggableId, type, source, destination }) => {
        if (
          !destination ||
          (source.droppableId === destination.droppableId && source.index === destination.index)
        ) {
          return;
        }

        const id = parseDndId(draggableId);

        switch (type) {
          case DroppableTypes.LIST:
            onListMove(id, destination.index);

            break;
          case DroppableTypes.CARD:
            if (source.droppableId !== destination.droppableId) {
              const fromListId = parseDndId(source.droppableId);
              const toListId = parseDndId(destination.droppableId);

              const fromReport = listReportDataById?.[String(fromListId)];
              const toReport = listReportDataById?.[String(toListId)];

              const fromYear = fromReport?.year ? Number(fromReport.year) : null;
              const fromMonth = fromReport?.month ? Number(fromReport.month) : null;
              const toYear = toReport?.year ? Number(toReport.year) : null;
              const toMonth = toReport?.month ? Number(toReport.month) : null;

              const hasConflict =
                fromYear &&
                fromMonth &&
                toYear &&
                toMonth &&
                (fromYear !== toYear || fromMonth !== toMonth);

              if (hasConflict) {
                setPendingCardMove({
                  cardId: id,
                  toListId: String(toListId),
                  toIndex: destination.index,
                  source: { year: fromYear, month: fromMonth },
                  destination: { year: toYear, month: toMonth },
                });
                setIsMoveConflictModalOpen(true);
                return;
              }

              onCardMove(id, String(toListId), destination.index);
              return;
            }

            onCardMove(id, parseDndId(destination.droppableId), destination.index);

            break;
          default:
        }
      },
      [onListMove, onCardMove, listReportDataById],
    );

    const handleMouseDown = useCallback(
      (event) => {
        // If button is defined and not equal to 0 (left click)
        if (event.button) {
          return;
        }

        if (event.target !== wrapper.current && !event.target.dataset.dragScroller) {
          return;
        }

        prevPosition.current = event.clientX;
      },
      [wrapper],
    );

    const handleWindowMouseMove = useCallback(
      (event) => {
        if (!prevPosition.current) {
          return;
        }

        event.preventDefault();

        window.scrollBy({
          left: prevPosition.current - event.clientX,
        });

        prevPosition.current = event.clientX;
      },
      [prevPosition],
    );

    const handleWindowMouseUp = useCallback(() => {
      prevPosition.current = null;
    }, [prevPosition]);

    useEffect(() => {
      document.body.style.overflowX = 'auto';
      document.body.classList.add('board-open');

      return () => {
        document.body.style.overflowX = null;
        document.body.classList.remove('board-open');
      };
    }, []);

    useEffect(() => {
      if (isListAddOpened) {
        window.scroll(document.body.scrollWidth, 0);
      }
    }, [listIds, isListAddOpened]);

    useEffect(() => {
      window.addEventListener('mouseup', handleWindowMouseUp);
      window.addEventListener('mousemove', handleWindowMouseMove);

      return () => {
        window.removeEventListener('mouseup', handleWindowMouseUp);
        window.removeEventListener('mousemove', handleWindowMouseMove);
      };
    }, [handleWindowMouseUp, handleWindowMouseMove]);

    return (
      <>
        <div ref={wrapper} className={styles.wrapper} onMouseDown={handleMouseDown}>
          <div>
            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <Droppable droppableId="board" type={DroppableTypes.LIST} direction="horizontal">
                {({ innerRef, droppableProps, placeholder }) => (
                  <div
                    {...droppableProps} // eslint-disable-line react/jsx-props-no-spreading
                    data-drag-scroller
                    ref={innerRef}
                    className={styles.lists}
                  >
                    {listIds.map((listId, index) => (
                      <ListContainer key={listId} id={listId} index={index} />
                    ))}
                    {placeholder}
                    {canEdit && (
                      <div data-drag-scroller className={styles.list}>
                        {isListAddOpened ? (
                          <ListAdd onCreate={onListCreate} onClose={handleAddListClose} />
                        ) : (
                          <button
                            type="button"
                            className={styles.addListButton}
                            onClick={handleAddListClick}
                          >
                            <PlusMathIcon className={styles.addListButtonIcon} />
                            <span className={styles.addListButtonText}>
                              {listIds.length > 0
                                ? t('action.addAnotherList')
                                : t('action.addList')}
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
        <Modal
          open={isMoveConflictModalOpen}
          onClose={handlePendingMoveCancel}
          size="small"
          centered={false}
          closeOnDimmerClick={false}
          closeIcon
        >
          <Modal.Header>Подтверждение</Modal.Header>
          <Modal.Content>
            <div>Вы переносите карточку в список другого месяца.</div>
            {moveConflictText && <div style={{ marginTop: '8px', fontWeight: 'bold' }}>{moveConflictText}</div>}
            <div style={{ marginTop: '8px' }}>Продолжить перенос?</div>
          </Modal.Content>
          <Modal.Actions>
            <Button type="button" onClick={handlePendingMoveCancel}>
              Отменить
            </Button>
            <Button type="button" primary onClick={handlePendingMoveContinue} disabled={!pendingCardMove}>
              Продолжить
            </Button>
          </Modal.Actions>
        </Modal>
        {isCardModalOpened && <CardModalContainer />}
      </>
    );
  },
);

Board.propTypes = {
  listIds: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  listReportDataById: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isCardModalOpened: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onListCreate: PropTypes.func.isRequired,
  onListMove: PropTypes.func.isRequired,
  onCardMove: PropTypes.func.isRequired,
};

export default Board;
